import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import { LangContext } from '../App';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// ── Translations ───────────────────────────────────────────────────────────────

const T = {
  fr: {
    title: 'Mon abonnement',
    sousTitre: "Gérez votre level d'accès et vos avantages.",
    retour: '← Tableau de bord',
    planActuel: 'Plan actuel',
    expireLE: 'Expire le',
    neverExpires: 'Aucune expiration',
    chargement: 'Chargement…',
    erreur: 'Impossible de charger votre abonnement.',
    upgradeTitle: 'Passer au level supérieur',
    monthly: 'Mensuel',
    annual: 'Annuel (économisez ~17%)',
    perMonth: '/ mois',
    perYear: '/ an',
    buyStripe: 'Payer par carte (Stripe)',
    buyCredits: 'Payer en crédits',
    processing: 'Traitement…',
    insuffisant: 'Crédits insuffisants',
    successTitle: 'Abonnement activé !',
    successDesc: 'Votre abonnement a été mis à jour avec succès.',
    cancelMsg: 'Paiement annulé.',
    creditsBalance: 'Solde crédits',
    features: {
      standard: ['Accès carte des terres', 'Soumission observations', 'Tableau de bord de base'],
      premium: [
        'Tout Standard',
        'Carte personnelle interactive',
        "Fil d'activité social",
        'Statistiques régionales avancées',
      ],
      premium_plus: [
        'Tout Premium',
        'Accès illimité aux spots de pêche',
        'Priorité dans le fil communautaire',
        'Support prioritaire',
      ],
    },
    plans: {
      standard: 'Standard',
      premium: 'Premium',
      premium_plus: 'Premium Plus',
      pro: 'Pro',
    },
    creditsPrice: (n) => `${n} crédits / an`,
    historyTitle: 'Historique des abonnements',
    noHistory: "Aucun historique d'abonnement.",
    level: 'Niveau',
    from: 'Du',
    to: 'Au',
    paymentMethod: 'Méthode',
    methods: {
      credits: 'Crédits',
      stripe_mensuel: 'Stripe mensuel',
      stripe_annuel: 'Stripe annuel',
    },
    active: 'Actif',
  },
  en: {
    title: 'My Subscription',
    sousTitre: 'Manage your access level and benefits.',
    retour: '← Dashboard',
    planActuel: 'Current plan',
    expireLE: 'Expires on',
    neverExpires: 'No expiry',
    chargement: 'Loading…',
    erreur: 'Failed to load your subscription.',
    upgradeTitle: 'Upgrade your plan',
    monthly: 'Monthly',
    annual: 'Annual (save ~17%)',
    perMonth: '/ month',
    perYear: '/ year',
    buyStripe: 'Pay by card (Stripe)',
    buyCredits: 'Pay with credits',
    processing: 'Processing…',
    insuffisant: 'Insufficient credits',
    successTitle: 'Subscription activated!',
    successDesc: 'Your subscription has been updated successfully.',
    cancelMsg: 'Payment cancelled.',
    creditsBalance: 'Credits balance',
    features: {
      standard: ['Land map access', 'Submit observations', 'Basic dashboard'],
      premium: [
        'Everything in Standard',
        'Interactive personal map',
        'Social activity feed',
        'Advanced regional statistics',
      ],
      premium_plus: [
        'Everything in Premium',
        'Unlimited fishing spot access',
        'Priority in community feed',
        'Priority support',
      ],
    },
    plans: {
      standard: 'Standard',
      premium: 'Premium',
      premium_plus: 'Premium Plus',
      pro: 'Pro',
    },
    creditsPrice: (n) => `${n} credits / year`,
    historyTitle: 'Subscription history',
    noHistory: 'No subscription history.',
    level: 'Level',
    from: 'From',
    to: 'To',
    paymentMethod: 'Method',
    methods: {
      credits: 'Credits',
      stripe_mensuel: 'Stripe monthly',
      stripe_annuel: 'Stripe annual',
    },
    active: 'Active',
  },
};

// ── Constants ──────────────────────────────────────────────────────────────────

const PLAN_ORDER = ['standard', 'premium', 'premium_plus'];
const PLAN_COLORS = {
  standard: { border: '#444', bg: '#1a1a1a', accent: '#888', text: '#ccc' },
  premium: { border: '#c9a227', bg: 'rgba(201,162,39,.07)', accent: '#c9a227', text: '#c9a227' },
  premium_plus: {
    border: '#6090ee',
    bg: 'rgba(96,144,238,.07)',
    accent: '#80b0ff',
    text: '#80b0ff',
  },
};

const STRIPE_PRICES = {
  premium: { monthly: 10, annual: 100 },
  premium_plus: { monthly: 30, annual: 250 },
};
const CREDITS_PRICES = {
  premium: 1200,
  premium_plus: 3000,
};

// ── Component ──────────────────────────────────────────────────────────────────

export default function MySubscription() {
  const { lang } = useContext(LangContext);
  const { utilisateur } = useAuth();
  const t = T[lang] || T.fr;

  const [searchParams] = useSearchParams();
  const stripeResult = searchParams.get('stripe');
  const stripePlan = searchParams.get('plan');

  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [billing, setBilling] = useState('annual');
  const [purchasing, setPurchasing] = useState(null); // 'stripe-premium' | 'credits-premium' | etc.
  const [purchaseErr, setPurchaseErr] = useState('');
  const [purchaseOk, setPurchaseOk] = useState(stripeResult === 'success');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [creditsRes, historyRes] = await Promise.all([
        api.get('/me/credits'),
        api.get('/me/subscription-history').catch(() => ({ data: { data: [] } })),
      ]);
      setData(creditsRes.data);
      setHistory(historyRes.data?.data || []);
    } catch {
      setError(t.erreur);
    } finally {
      setLoading(false);
    }
  }, [t.erreur]);

  useEffect(() => {
    load();
  }, [load]);

  // Activate via credits
  const handleBuyCredits = async (plan) => {
    const key = `credits-${plan}`;
    setPurchasing(key);
    setPurchaseErr('');
    try {
      await api.post('/me/credits/acheter-abonnement', { level: plan });
      setPurchaseOk(true);
      await load();
    } catch (err) {
      setPurchaseErr(err?.response?.data?.error || err.message);
    } finally {
      setPurchasing(null);
    }
  };

  // Redirect to Stripe checkout
  const handleBuyStripe = async (plan) => {
    const key = `stripe-${plan}`;
    setPurchasing(key);
    setPurchaseErr('');
    try {
      const { data: d } = await api.post('/stripe/subscription/checkout', { plan, billing });
      window.location.href = d.url;
    } catch (err) {
      setPurchaseErr(err?.response?.data?.error || err.message);
      setPurchasing(null);
    }
  };

  const currentPlan = data?.subscription_tier || utilisateur?.subscription_tier || 'standard';
  const expiresOn = data?.subscription_expires_at;
  const creditBal = data?.credits_balance ?? 0;

  const upgradablePlans = PLAN_ORDER.filter((p) => {
    const currentIdx = PLAN_ORDER.indexOf(currentPlan);
    const planIdx = PLAN_ORDER.indexOf(p);
    return planIdx > currentIdx;
  });

  return (
    <PrivateLayout>
      <main style={{ maxWidth: 880, margin: '0 auto', paddingBottom: '4rem' }}>
        <PageHeader icon="💎" title={t.title} subtitle={t.sousTitre} />

        {/* Stripe result banners */}
        {stripeResult === 'cancel' && (
          <div
            style={{
              background: 'rgba(200,50,50,.1)',
              border: '1px solid rgba(200,50,50,.35)',
              borderRadius: 8,
              padding: '.8rem 1rem',
              color: 'var(--couleur-texte-erreur)',
              marginBottom: '1.5rem',
            }}
          >
            {t.cancelMsg}
          </div>
        )}
        {purchaseOk && (
          <div
            style={{
              background: 'rgba(40,160,80,.1)',
              border: '1px solid rgba(40,160,80,.35)',
              borderRadius: 8,
              padding: '1rem 1.2rem',
              marginBottom: '1.5rem',
            }}
          >
            <div
              style={{
                fontWeight: 700,
                color: 'var(--couleur-texte-succes)',
                marginBottom: '.25rem',
              }}
            >
              {t.successTitle}
            </div>
            <div style={{ fontSize: '.88rem', color: 'var(--couleur-texte-attenué)' }}>
              {t.successDesc}
            </div>
          </div>
        )}
        {purchaseErr && (
          <div
            style={{
              background: 'rgba(200,50,50,.1)',
              border: '1px solid rgba(200,50,50,.35)',
              borderRadius: 8,
              padding: '.8rem 1rem',
              color: 'var(--couleur-texte-erreur)',
              marginBottom: '1.5rem',
            }}
          >
            {purchaseErr}
          </div>
        )}

        {loading && <p style={{ color: 'var(--couleur-texte-attenué)' }}>{t.chargement}</p>}
        {error && <p style={{ color: 'var(--couleur-texte-erreur)' }}>{error}</p>}

        {!loading && !error && data && (
          <>
            {/* Current plan card */}
            <section
              style={{
                background: 'var(--couleur-fond)',
                borderRadius: 'var(--rayon)',
                padding: '1.5rem',
                marginBottom: '2rem',
                display: 'flex',
                gap: '2rem',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: '.75rem',
                    color: 'var(--couleur-texte-attenué)',
                    letterSpacing: '.08em',
                    marginBottom: '.4rem',
                  }}
                >
                  {t.planActuel.toUpperCase()}
                </div>
                <div
                  style={{
                    fontSize: '1.8rem',
                    fontWeight: 800,
                    color: PLAN_COLORS[currentPlan]?.accent || 'var(--couleur-or)',
                  }}
                >
                  {t.plans[currentPlan] || currentPlan}
                </div>
                {expiresOn && (
                  <div
                    style={{
                      fontSize: '.8rem',
                      color: 'var(--couleur-texte-attenué)',
                      marginTop: '.3rem',
                    }}
                  >
                    {t.expireLE}:{' '}
                    {new Date(expiresOn).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA')}
                  </div>
                )}
              </div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div
                  style={{
                    fontSize: '.75rem',
                    color: 'var(--couleur-texte-attenué)',
                    letterSpacing: '.08em',
                    marginBottom: '.4rem',
                  }}
                >
                  {t.creditsBalance.toUpperCase()}
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--couleur-or)' }}>
                  {creditBal.toLocaleString()}
                </div>
              </div>
            </section>

            {/* Plan comparison + upgrade */}
            {upgradablePlans.length > 0 && (
              <section style={{ marginBottom: '2rem' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                    flexWrap: 'wrap',
                    gap: '.5rem',
                  }}
                >
                  <h2 style={{ color: 'var(--couleur-or)', fontSize: '1rem', margin: 0 }}>
                    {t.upgradeTitle}
                  </h2>
                  {/* Billing toggle */}
                  <div
                    style={{
                      display: 'flex',
                      background: '#1a1a1a',
                      borderRadius: 20,
                      padding: 3,
                      border: '1px solid #333',
                    }}
                  >
                    {['monthly', 'annual'].map((b) => (
                      <button
                        key={b}
                        onClick={() => setBilling(b)}
                        style={{
                          padding: '.3rem .9rem',
                          borderRadius: 20,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '.82rem',
                          fontWeight: billing === b ? 700 : 400,
                          background: billing === b ? 'var(--couleur-or)' : 'transparent',
                          color: billing === b ? '#000' : 'var(--couleur-texte-attenué)',
                          transition: 'all .2s',
                        }}
                      >
                        {t[b]}
                      </button>
                    ))}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: '1rem',
                  }}
                >
                  {upgradablePlans.map((plan) => {
                    const colors = PLAN_COLORS[plan];
                    const stripePrice = STRIPE_PRICES[plan]?.[billing];
                    const creditsPrice = CREDITS_PRICES[plan];
                    const canAfford = creditBal >= creditsPrice;
                    const stripeKey = `stripe-${plan}`;
                    const creditsKey = `credits-${plan}`;

                    return (
                      <div
                        key={plan}
                        style={{
                          background: colors.bg,
                          border: `1px solid ${colors.border}`,
                          borderRadius: 12,
                          padding: '1.5rem',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 800,
                            fontSize: '1.15rem',
                            color: colors.accent,
                            marginBottom: '.75rem',
                          }}
                        >
                          {t.plans[plan]}
                        </div>

                        {/* Features */}
                        <ul
                          style={{
                            margin: '0 0 1.25rem',
                            padding: '0 0 0 1rem',
                            listStyle: 'none',
                          }}
                        >
                          {t.features[plan]?.map((f, i) => (
                            <li
                              key={i}
                              style={{
                                fontSize: '.83rem',
                                color: 'var(--couleur-texte-attenué)',
                                marginBottom: '.3rem',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '.4rem',
                              }}
                            >
                              <span
                                style={{ color: colors.accent, fontWeight: 700, flexShrink: 0 }}
                              >
                                ✓
                              </span>{' '}
                              {f}
                            </li>
                          ))}
                        </ul>

                        {/* Stripe price */}
                        {stripePrice && (
                          <div style={{ marginBottom: '.75rem' }}>
                            <div
                              style={{ fontSize: '1.4rem', fontWeight: 800, color: colors.accent }}
                            >
                              {stripePrice} $ CAD
                              <span
                                style={{
                                  fontSize: '.8rem',
                                  fontWeight: 400,
                                  color: 'var(--couleur-texte-attenué)',
                                }}
                              >
                                {' '}
                                {billing === 'monthly' ? t.perMonth : t.perYear}
                              </span>
                            </div>
                            <button
                              onClick={() => handleBuyStripe(plan)}
                              disabled={!!purchasing}
                              style={{
                                width: '100%',
                                marginTop: '.5rem',
                                padding: '.55rem',
                                background: colors.accent,
                                color: '#000',
                                border: 'none',
                                borderRadius: 8,
                                fontWeight: 700,
                                fontSize: '.88rem',
                                cursor: purchasing ? 'wait' : 'pointer',
                              }}
                            >
                              {purchasing === stripeKey ? t.processing : t.buyStripe}
                            </button>
                          </div>
                        )}

                        {/* Credits price */}
                        <div style={{ borderTop: '1px solid #2a2a2a', paddingTop: '.75rem' }}>
                          <div
                            style={{
                              fontSize: '.82rem',
                              color: 'var(--couleur-texte-attenué)',
                              marginBottom: '.4rem',
                            }}
                          >
                            {t.creditsPrice(creditsPrice)}
                          </div>
                          <button
                            onClick={() => handleBuyCredits(plan)}
                            disabled={!!purchasing || !canAfford}
                            style={{
                              width: '100%',
                              padding: '.5rem',
                              background: canAfford ? '#2a2a2a' : '#1a1a1a',
                              color: canAfford ? colors.accent : '#555',
                              border: `1px solid ${canAfford ? colors.border : '#333'}`,
                              borderRadius: 8,
                              fontWeight: 600,
                              fontSize: '.85rem',
                              cursor: purchasing || !canAfford ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {purchasing === creditsKey
                              ? t.processing
                              : !canAfford
                                ? t.insuffisant
                                : t.buyCredits}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* All plans overview when already max */}
            {upgradablePlans.length === 0 && (
              <div
                style={{
                  background: 'rgba(40,160,80,.08)',
                  border: '1px solid rgba(40,160,80,.3)',
                  borderRadius: 12,
                  padding: '1.5rem',
                  marginBottom: '2rem',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: 'var(--couleur-texte-succes)',
                    marginBottom: '.4rem',
                  }}
                >
                  {t.plans[currentPlan]} — {t.active}
                </div>
                <div style={{ color: 'var(--couleur-texte-attenué)', fontSize: '.9rem' }}>
                  {t.features[currentPlan]?.join(' · ')}
                </div>
              </div>
            )}

            {/* Subscription history */}
            <section>
              <h2 style={{ color: 'var(--couleur-or)', fontSize: '1rem', marginBottom: '1rem' }}>
                {t.historyTitle}
              </h2>
              {history.length === 0 ? (
                <p style={{ color: 'var(--couleur-texte-attenué)', fontSize: '.88rem' }}>
                  {t.noHistory}
                </p>
              ) : (
                <div
                  style={{
                    background: 'var(--couleur-fond)',
                    borderRadius: 'var(--rayon)',
                    overflow: 'hidden',
                  }}
                >
                  {history.map((h, i) => (
                    <div
                      key={h.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr 1fr',
                        gap: '.75rem',
                        padding: '.85rem 1.1rem',
                        borderBottom: i < history.length - 1 ? '1px solid #2a2a2a' : 'none',
                        fontSize: '.83rem',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          color: PLAN_COLORS[h.level]?.accent || 'var(--couleur-texte)',
                          fontWeight: 600,
                        }}
                      >
                        {t.plans[h.level] || h.level}
                      </span>
                      <span style={{ color: 'var(--couleur-texte-attenué)' }}>{h.start_date}</span>
                      <span style={{ color: 'var(--couleur-texte-attenué)' }}>
                        {h.end_date || '—'}
                      </span>
                      <span style={{ color: 'var(--couleur-texte-attenué)' }}>
                        {t.methods[h.payment_method] || h.payment_method || '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </PrivateLayout>
  );
}
